/**
 * EditSchemaDialog Component
 *
 * Dialog for editing an existing multi-field Schema.
 * Features:
 * - Pre-populated with schema data
 * - Version and status display
 * - Warning for published schemas
 * - Multiple field management with add/remove/reorder
 * - Drag-and-drop field reordering
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Download, AlertTriangle, FileCode } from 'lucide-react';
import {
  schemaApi,
  Schema,
  SchemaField,
  createDefaultField,
  SCHEMA_STATUSES,
} from '@/services/api/schemaApi';
import { AdminSchemaFieldEditor } from './AdminSchemaFieldEditor';
import { ImportFieldsDialog } from './ImportFieldsDialog';
import { cn } from '@/lib/utils';
import * as errorUtils from '@/utils/errors';

interface EditSchemaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema: Schema;
  onSuccess: () => void;
}

export const EditSchemaDialog: React.FC<EditSchemaDialogProps> = ({
  open,
  onOpenChange,
  schema,
  onSuccess,
}) => {
  // Form state
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState<SchemaField[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Drag state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Initialize form with schema data
  useEffect(() => {
    if (open && schema) {
      setDisplayName(schema.displayName || '');
      setDescription(schema.description || '');
      setFields(schema.fields?.length > 0 ? [...schema.fields] : [createDefaultField()]);
      setError(null);
    }
  }, [open, schema]);

  // Status config
  const statusConfig = SCHEMA_STATUSES.find((s) => s.value === schema.status) || SCHEMA_STATUSES[0];
  const isPublished = schema.status === 'published';
  const isDeprecated = schema.status === 'deprecated';

  // Handle dialog close
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      onOpenChange(isOpen);
    },
    [onOpenChange]
  );

  // Field management
  const handleAddField = useCallback(() => {
    setFields((prev) => [...prev, createDefaultField()]);
  }, []);

  const handleUpdateField = useCallback((index: number, updatedField: SchemaField) => {
    setFields((prev) => prev.map((f, i) => (i === index ? updatedField : f)));
  }, []);

  const handleRemoveField = useCallback((index: number) => {
    setFields((prev) => {
      if (prev.length <= 1) return prev; // Keep at least one field
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Drag and drop handlers
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === dropIndex) return;

      setFields((prev) => {
        const newFields = [...prev];
        const [removed] = newFields.splice(dragIndex, 1);
        newFields.splice(dropIndex, 0, removed);
        return newFields;
      });

      setDragIndex(null);
      setDragOverIndex(null);
    },
    [dragIndex]
  );

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragOverIndex(null);
  }, []);

  // Import fields from ImportFieldsDialog (merge with overwrite)
  const handleImportFields = useCallback((importedFields: SchemaField[]) => {
    setFields((prevFields) => {
      const result = [...prevFields];

      for (const imported of importedFields) {
        const existingIndex = result.findIndex((f) => f.name === imported.name);
        if (existingIndex >= 0) {
          // Overwrite existing field, keep the existing ID
          result[existingIndex] = { ...imported, id: result[existingIndex].id };
        } else {
          // Add new field
          result.push(imported);
        }
      }

      return result;
    });
  }, []);

  // Validation
  const validateForm = useCallback((): string | null => {
    if (!displayName.trim()) {
      return 'Display name is required';
    }

    if (!description.trim()) {
      return 'Description is required';
    }

    if (fields.length === 0) {
      return 'At least one field is required';
    }

    // Validate each field
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (!field.name.trim()) {
        return `Field ${i + 1} is missing a name`;
      }
      if (!/^[a-z][a-z0-9_]*$/.test(field.name)) {
        return `Field "${field.name}" must be lowercase with underscores`;
      }
    }

    // Check for duplicate field names
    const fieldNames = fields.map((f) => f.name);
    const duplicates = fieldNames.filter((name, index) => fieldNames.indexOf(name) !== index);
    if (duplicates.length > 0) {
      return `Duplicate field name: ${duplicates[0]}`;
    }

    // Per-field storage: when a Date field declares any storage property,
    // it must declare all three (timezone, format, hasTimezoneMarker). The
    // backend rejects partial declarations; catch them here for a clearer
    // message and to point at the offending field.
    for (const f of fields) {
      if (f.type !== 'date' && f.type !== 'datetime') continue;
      const s = f.storage;
      if (!s) continue;
      const hasTz = !!s.timezone;
      const hasFmt = !!s.format;
      const hasMarker = typeof s.hasTimezoneMarker === 'boolean';
      const anySet = hasTz || hasFmt || hasMarker;
      const allSet = hasTz && hasFmt && hasMarker;
      if (anySet && !allSet) {
        return `Field "${f.name}" storage: set Timezone, Format, and the timezone-marker checkbox together — or leave all three empty.`;
      }
    }

    return null;
  }, [displayName, description, fields]);

  // Submit handler
  const handleSubmit = useCallback(async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await schemaApi.updateSchema(schema.id, {
        displayName: displayName.trim(),
        description: description.trim(),
        fields,
      });

      onSuccess();
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to update schema');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [schema.id, displayName, description, fields, validateForm, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCode className="h-5 w-5" />
            Edit Schema
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-4 px-1">
          {/* Schema Info Banner */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{schema.name}</span>
                <span
                  className={cn(
                    'text-xs font-medium px-2 py-0.5 rounded',
                    statusConfig.color
                  )}
                >
                  {statusConfig.label}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                Version {schema.currentVersion}
                {schema.latestPublishedVersion > 0 && (
                  <span> • Latest published: v{schema.latestPublishedVersion}</span>
                )}
              </div>
            </div>
          </div>

          {/* Warning for published schemas */}
          {isPublished && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-900">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium">Editing a Published Schema</div>
                <div className="mt-1">
                  Changes will create a new draft version. The published version will remain active until you publish the new version.
                </div>
              </div>
            </div>
          )}

          {/* Warning for deprecated schemas */}
          {isDeprecated && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 text-gray-800 dark:bg-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium">This Schema is Deprecated</div>
                {schema.deprecatedReason && (
                  <div className="mt-1">Reason: {schema.deprecatedReason}</div>
                )}
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium border-b pb-2">Basic Information</h3>

            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name *</Label>
              <Input
                id="displayName"
                placeholder="e.g., Sensor Data"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Technical Name</Label>
              <Input
                value={schema.name}
                disabled
                className="font-mono bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Technical name cannot be changed after creation
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe what data this schema represents..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          {/* Fields Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-sm font-medium">
                Fields ({fields.length})
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowImportDialog(true)}
                  className="gap-1"
                >
                  <Download className="h-4 w-4" />
                  Import
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddField}
                  className="gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add Field
                </Button>
              </div>
            </div>

            {fields.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No fields defined.</p>
                <p className="text-sm">Click "Add Field" to create your first field.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {fields.map((field, index) => (
                  <AdminSchemaFieldEditor
                    key={field.id}
                    field={field}
                    index={index}
                    onChange={(updated) => handleUpdateField(index, updated)}
                    onRemove={() => handleRemoveField(index)}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    isDragging={dragIndex === index}
                    isDragOver={dragOverIndex === index}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Import Fields Dialog */}
      <ImportFieldsDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        existingFields={fields}
        onImport={handleImportFields}
        excludeSchemaId={schema.id}
      />
    </Dialog>
  );
};

export default EditSchemaDialog;
